/** @jsx jsx */
import { memo, useState } from 'react';
import { jsx } from '@emotion/react';
import Button from '@atlaskit/button/standard-button';
import Heading from '@atlaskit/heading';
import ArrowleftIcon from '@atlaskit/icon/glyph/chevron-left-large';
import ArrowrightIcon from '@atlaskit/icon/glyph/chevron-right-large';
import { Box, Inline } from '@atlaskit/primitives';
import { N700 } from '@atlaskit/theme/colors';
import useUniqueId from '../../internal/hooks/use-unique-id';
const Header = /*#__PURE__*/memo(function Header({
  monthLongTitle,
  year,
  previousMonthLabel = 'Previous month',
  previousHeading,
  nextMonthLabel = 'Next month',
  nextHeading,
  handleClickPrev,
  handleClickNext,
  headerId,
  tabIndex,
  testId
}) {
  const announceId = useUniqueId('month-year-announce');

  // All of this is because `aria-atomic` is not fully supported for different
  // assistive technologies. We want the value of the current month and year to
  // be announced, but *only* if that value has been interacted with since
  // being mounted. This allows us to conditionally apply the `aria-live`
  // attribute.  Without this, the `aria-live` property is set on mount and
  // overrides the default input's readout in downstream consumers (e.g.
  // datetime picker).
  const [hasInteractedWithMonth, setHasInteractedWithMonth] = useState(false);
  const handlePrevMonthInteraction = e => {
    if (!hasInteractedWithMonth) {
      setHasInteractedWithMonth(true);
    }
    handleClickPrev(e);
  };
  const handleNextMonthInteraction = e => {
    if (!hasInteractedWithMonth) {
      setHasInteractedWithMonth(true);
    }
    handleClickNext(e);
  };
  return jsx(Box, {
    paddingInline: "space.100"
  }, jsx(Inline, {
    space: "space.0",
    alignBlock: "center",
    spread: "space-between"
  }, jsx(Button, {
    appearance: "subtle",
    spacing: "none",
    tabIndex: tabIndex,
    onClick: handlePrevMonthInteraction,
    testId: testId && `${testId}--previous-month`,
    iconBefore: jsx(ArrowleftIcon, {
      label: `${previousMonthLabel}, ${previousHeading}`,
      size: "medium",
      primaryColor: `var(--ds-icon, ${N700})`,
      testId: testId && `${testId}--previous-month-icon`
    })
  }), jsx(Box, {
    "aria-live": hasInteractedWithMonth ? 'polite' : undefined,
    id: announceId,
    testId: testId && `${testId}--current-month-year--container`
  }, jsx(Heading, {
    level: "h400",
    as: "h2",
    id: headerId,
    testId: testId && `${testId}--current-month-year`
  }, `${monthLongTitle} ${year}`)), jsx(Button, {
    appearance: "subtle",
    spacing: "none",
    tabIndex: tabIndex,
    onClick: handleNextMonthInteraction,
    testId: testId && `${testId}--next-month`,
    iconBefore: jsx(ArrowrightIcon, {
      label: `${nextMonthLabel}, ${nextHeading}`,
      size: "medium",
      primaryColor: `var(--ds-icon, ${N700})`,
      testId: testId && `${testId}--next-month-icon`
    })
  })));
});
Header.displayName = 'Header';

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default Header;